import { useTranslate } from "@refinedev/core";
import { Modal } from "@libertify/ui";
import type { TeacherProfile } from "pages/creator-profile/types";
import { CreatorDnaModalFooter } from "./CreatorDnaModalFooter";
import { CreatorDnaDetails } from "./CreatorDnaDetails";
import { CreatorDnaEdit } from "./CreatorDnaEdit";

export type CreatorDnaModalMode = "view" | "edit";

export type CreatorDnaModalProps = Readonly<{
  /** Which content to show; null keeps the modal closed. */
  mode: CreatorDnaModalMode | null;
  profile?: TeacherProfile;
  onModeChange: (mode: CreatorDnaModalMode) => void;
  onClose: () => void;
}>;

export const CreatorDnaModal = ({
  mode,
  profile,
  onModeChange,
  onClose,
}: CreatorDnaModalProps) => {
  const t = useTranslate();

  const renderTitle = () => {
    switch (mode) {
      case "view":
        return t("pages.user.edit.creatorDna.modalTitle");
      case "edit":
        return t("pages.user.edit.creatorDna.edit");
      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (mode) {
      case "view":
        return profile?.archetype ? (
          <CreatorDnaDetails archetype={profile.archetype} />
        ) : null;
      case "edit":
        return profile ? (
          <CreatorDnaEdit
            profile={profile}
            onClose={onClose}
            onSaved={() => onModeChange("view")}
          />
        ) : null;
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (mode) {
      case "view":
        return (
          <CreatorDnaModalFooter
            closeLabel={t("buttons.close")}
            actionLabel={t("pages.user.edit.creatorDna.edit")}
            onClose={onClose}
            onAction={() => onModeChange("edit")}
          />
        );
      case "edit":
        // The edit form renders its own footer: it owns the state Save needs.
        return null;
      default:
        return null;
    }
  };

  return (
    <Modal
      open={mode !== null}
      onClose={onClose}
      title={renderTitle()}
      size="xlarge"
      footer={renderFooter()}
      styles={{ content: { padding: 0 } }}
      destroyOnHidden
    >
      {renderContent()}
    </Modal>
  );
};
